import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Bot, X, Send, Loader2, UserRound, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Link } from "@/lib/router-compat";

type Msg = { role: "user" | "assistant"; content: string };

export default function AISupportWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: "Hi! I'm the Sentinel Forge AI support agent. Ask me anything about the platform — proposals, billing questions, features, or how-to. If I can't help, I'll loop in a human teammate." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [escalatedTicketId, setEscalatedTicketId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // Lock body scroll while the chat is open so swiping inside the panel
  // doesn't scroll the page behind it (mobile scroll chaining).
  useEffect(() => {
    if (!open) return;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "contain";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, [open]);

  if (!user) return null;

  const send = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-support-agent", {
        body: { mode: "chat", session_id: sessionId, messages: next },
      });
      if (error) throw error;
      if (data?.session_id) setSessionId(data.session_id);
      const reply = data?.reply ?? "Sorry, something went wrong.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      if (data?.escalated && data?.ticket_id) {
        setEscalatedTicketId(data.ticket_id);
        toast.success("Handed off to a human teammate", {
          description: "Your conversation has been turned into a support ticket.",
        });
      }
    } catch (e: any) {
      toast.error("AI support is unavailable", { description: e.message });
      setMessages((m) => [...m, { role: "assistant", content: "I hit an error. Please try again or open a regular support ticket." }]);
    } finally {
      setSending(false);
    }
  };

  const requestHuman = () => send("Please connect me with a human agent.");

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] sm:bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg hover:bg-primary/90 transition-all"
          aria-label="Open AI support chat"
        >
          <Bot className="h-5 w-5" />
          <span className="font-semibold text-sm hidden sm:inline">AI Support</span>
        </button>
      )}

      {open && (
        <Card className="fixed z-50 bg-slate-900 border-slate-700 shadow-2xl flex flex-col
          inset-x-2 bottom-2 top-16
          sm:inset-auto sm:bottom-4 sm:right-4 sm:top-auto sm:w-[400px] sm:h-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-slate-800 rounded-t-lg">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Bot className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">AI Support</div>
                <div className="text-[11px] text-slate-400">Tier-1 — escalates to a human if needed</div>
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain p-3 space-y-3" style={{ WebkitOverflowScrolling: "touch" }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-amber-500 text-slate-900"
                    : "bg-slate-800 text-slate-100 border border-slate-700"
                }`}>
                  {m.role === "assistant" ? (
                    <div className="prose prose-sm prose-invert max-w-none [&>*]:my-1 [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Thinking...
                </div>
              </div>
            )}
            {escalatedTicketId && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-200">
                Ticket created. <Link to="/support" className="underline font-semibold">View in Support →</Link>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-slate-700 p-2 space-y-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask a question..."
              rows={2}
              className="bg-slate-800 border-slate-700 text-white resize-none text-sm"
              disabled={sending}
            />
            <div className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={requestHuman}
                disabled={sending}
                className="text-slate-400 hover:text-amber-400 text-xs"
              >
                <UserRound className="h-3 w-3 mr-1" /> Talk to human
              </Button>
              <div className="flex items-center gap-2">
                <Link to="/support">
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white text-xs">
                    <MessageSquare className="h-3 w-3 mr-1" /> Tickets
                  </Button>
                </Link>
                <Button
                  size="sm"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  <Send className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
